import axios from "axios";

const setupApi = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json"
  }
});

const configFor = token => ({
  headers: token
    ? {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    : {
        "Content-Type": "application/json"
      }
});

const normalizedArray = value => (Array.isArray(value) ? value : []);
const normalizedName = value => String(value || "").trim().toLocaleLowerCase();
const hhmm = value => String(value || "").slice(0, 5);

const sameSchedule = (existing, wanted) => {
  const dayKeys = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday"
  ];

  return (
    hhmm(existing?.start_time) === hhmm(wanted?.start_time) &&
    hhmm(existing?.end_time) === hhmm(wanted?.end_time) &&
    dayKeys.every(day => Boolean(existing?.[day]) === Boolean(wanted?.[day]))
  );
};

const chunkedAll = async (tasks, chunkSize = 8) => {
  for (let index = 0; index < tasks.length; index += chunkSize) {
    await Promise.all(tasks.slice(index, index + chunkSize).map(task => task()));
  }
};

export const restaurantSetupApi = {
  async getRestaurant(restaurantId, token) {
    const { data } = await setupApi.get(
      `/restaurants/restaurantfromuser/${restaurantId}`,
      configFor(token)
    );
    return data;
  },

  async patchRestaurant(restaurantId, payload, token) {
    const { data } = await setupApi.patch(
      `/restaurants/restaurantfromuser/${restaurantId}`,
      payload,
      configFor(token)
    );
    return data;
  },

  async getRooms(restaurantId, token) {
    const { data } = await setupApi.get(
      `/restaurants/${restaurantId}/placeoftable`,
      configFor(token)
    );
    return normalizedArray(data);
  },

  async createRoom(restaurantId, name, token) {
    const { data } = await setupApi.post(
      `/restaurants/${restaurantId}/placeoftable/`,
      {
        restaurant: Number(restaurantId),
        place_of_table: String(name).trim()
      },
      configFor(token)
    );
    return data;
  },

  async getTables(restaurantId, token) {
    const { data } = await setupApi.get(
      `/restaurants/${restaurantId}/tables/`,
      configFor(token)
    );
    return normalizedArray(data);
  },

  async createTables(restaurantId, roomId, amount, seats, token) {
    if (Number(amount) <= 0) return null;
    const { data } = await setupApi.post(
      `/restaurants/${restaurantId}/tables/`,
      {
        place_of_table: Number(roomId),
        num_tables_to_create: Number(amount),
        num_seats: Number(seats) || 2
      },
      configFor(token)
    );
    return data;
  },

  async bulkUpdateTables(restaurantId, updates, token) {
    if (!updates.length) return [];
    const { data } = await setupApi.patch(
      `/restaurants/${restaurantId}/tables/bulk-update/`,
      { updates },
      configFor(token)
    );
    return normalizedArray(data);
  },

  async ensureRoomsAndTables(restaurantId, roomDrafts, token) {
    let rooms = await this.getRooms(restaurantId, token);
    const roomIds = {};

    for (const roomDraft of roomDrafts) {
      const roomName = String(roomDraft.name || "").trim();
      let room = rooms.find(
        item => normalizedName(item?.place_of_table) === normalizedName(roomName)
      );

      if (!room) {
        await this.createRoom(restaurantId, roomName, token);
        rooms = await this.getRooms(restaurantId, token);
        room = rooms.find(
          item => normalizedName(item?.place_of_table) === normalizedName(roomName)
        );
      }

      if (!room?.id) {
        throw new Error(`Could not create room: ${roomName}`);
      }
      roomIds[roomName] = room.id;
    }

    let allTables = await this.getTables(restaurantId, token);

    for (const roomDraft of roomDrafts) {
      const roomName = String(roomDraft.name || "").trim();
      const roomId = roomIds[roomName];
      const wantedTables = Array.isArray(roomDraft.tables) ? roomDraft.tables : [];
      let roomTables = allTables
        .filter(table => Number(table.place_of_table) === Number(roomId))
        .sort(
          (a, b) =>
            Number(a.table_number || a.id || 0) - Number(b.table_number || b.id || 0)
        );

      if (roomTables.length < wantedTables.length) {
        const missing = wantedTables.length - roomTables.length;
        const firstMissingSeatValue =
          wantedTables[roomTables.length]?.seats || roomDraft.defaultSeats || 2;
        await this.createTables(
          restaurantId,
          roomId,
          missing,
          firstMissingSeatValue,
          token
        );
        allTables = await this.getTables(restaurantId, token);
        roomTables = allTables
          .filter(table => Number(table.place_of_table) === Number(roomId))
          .sort(
            (a, b) =>
              Number(a.table_number || a.id || 0) - Number(b.table_number || b.id || 0)
          );
      }

      const updates = wantedTables
        .map((wanted, index) => {
          const table = roomTables[index];
          if (!table?.id) return null;
          const wantedSeats = Number(wanted?.seats) || 1;
          if (Number(table.number_of_seats) === wantedSeats) return null;
          return {
            id: table.id,
            number_of_seats: wantedSeats
          };
        })
        .filter(Boolean);

      await this.bulkUpdateTables(restaurantId, updates, token);
    }

    return { rooms, roomIds };
  },

  async getOpenTimes(restaurantId, token) {
    const { data } = await setupApi.get(
      `/restaurants/${restaurantId}/opentimes`,
      configFor(token)
    );
    return normalizedArray(data);
  },

  async ensureSchedules(restaurantId, schedules, shared, roomNames, token) {
    let existing = await this.getOpenTimes(restaurantId, token);
    const dayKeys = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday"
    ];

    for (const schedule of schedules) {
      const selectedDays = new Set(schedule.days || []);
      const payload = {
        restaurant: Number(restaurantId),
        start_time: schedule.startTime,
        end_time: schedule.endTime,
        each_time: shared.slotInterval,
        list_of_zones: roomNames.join(",")
      };
      dayKeys.forEach(day => {
        payload[day] = selectedDays.has(day);
      });

      const match = existing.find(item => sameSchedule(item, payload));
      const savedSlotCapacity = Number(match?.accept_people_per_each_time);
      const fallbackSlotCapacity = Number(shared.fallbackSlotCapacity);
      payload.accept_people_per_each_time = shared.preserveSlotCapacity
        ? (savedSlotCapacity > 0
            ? savedSlotCapacity
            : fallbackSlotCapacity > 0
              ? fallbackSlotCapacity
              : Number(shared.slotCapacity))
        : Number(shared.slotCapacity);

      if (match?.id) {
        await setupApi.patch(
          `/restaurants/${restaurantId}/opentimes/${match.id}/`,
          payload,
          configFor(token)
        );
      } else {
        await setupApi.post(
          `/restaurants/${restaurantId}/opentimes/`,
          payload,
          configFor(token)
        );
      }
      existing = await this.getOpenTimes(restaurantId, token);
    }
  },

  async getEstimatedTimes(restaurantId, token) {
    const { data } = await setupApi.get(
      `/restaurants/${restaurantId}/estimatedtime`,
      configFor(token)
    );
    return normalizedArray(data);
  },

  async ensureAverageStay(restaurantId, averageMinutes, maxGroupSize, token) {
    const minutes = Math.max(15, Number(averageMinutes) || 90);
    const hours = Math.floor(minutes / 60)
      .toString()
      .padStart(2, "0");
    const mins = (minutes % 60).toString().padStart(2, "0");
    const estimatedTime = `${hours}:${mins}`;
    const maximum = Math.min(100, Math.max(1, Number(maxGroupSize) || 10));
    const existing = await this.getEstimatedTimes(restaurantId, token);
    const tasks = [];

    for (let people = 1; people <= maximum; people += 1) {
      const match = existing.find(item => Number(item.number_of_people) === people);
      if (match?.id) {
        if (hhmm(match.estimated_time) !== estimatedTime) {
          tasks.push(() =>
            setupApi.patch(
              `/restaurants/${restaurantId}/estimatedtime/${match.id}/`,
              { number_of_people: people, estimated_time: estimatedTime },
              configFor(token)
            )
          );
        }
      } else {
        tasks.push(() =>
          setupApi.post(
            `/restaurants/${restaurantId}/estimatedtime/`,
            {
              restaurant: Number(restaurantId),
              number_of_people: people,
              estimated_time: estimatedTime
            },
            configFor(token)
          )
        );
      }
    }

    await chunkedAll(tasks);
  },

  async getNotifications(restaurantId, token) {
    const { data } = await setupApi.get(
      `/restaurants/${restaurantId}/restaurant_notifications/`,
      configFor(token)
    );
    return normalizedArray(data);
  },

  async ensureNotifications(restaurantId, notificationDraft, restaurantName, token) {
    let existing = await this.getNotifications(restaurantId, token);
    const label = String(restaurantName || "Restaurant").trim();

    if (notificationDraft?.phoneNotifications?.enabled) {
      const countryCode = String(
        notificationDraft.phoneNotifications.countryCode || ""
      ).replace(/\D/g, "");
      const phoneNumber = String(
        notificationDraft.phoneNotifications.number || ""
      ).replace(/\D/g, "");
      const alreadyExists = existing.some(
        item =>
          String(item.phone_number || "").replace(/\D/g, "") === phoneNumber &&
          String(item.country_code || "").replace(/\D/g, "") === countryCode
      );

      if (!alreadyExists) {
        await setupApi.post(
          `/restaurants/${restaurantId}/restaurant_notifications/`,
          {
            restaurant: Number(restaurantId),
            email: "",
            name: label,
            phone_number: phoneNumber,
            country_code: countryCode,
            getEmails: false,
            getWhatsappNotifications: true,
            whatsapp_notification_token: "",
            isItConfirmed: false
          },
          configFor(token)
        );
        existing = await this.getNotifications(restaurantId, token);
      }
    }

    if (notificationDraft?.emailNotifications?.enabled) {
      const email = String(notificationDraft.emailNotifications.email || "")
        .trim()
        .toLowerCase();
      const alreadyExists = existing.some(
        item => String(item.email || "").trim().toLowerCase() === email
      );

      if (!alreadyExists) {
        await setupApi.post(
          `/restaurants/${restaurantId}/restaurant_notifications/`,
          {
            restaurant: Number(restaurantId),
            email,
            name: label,
            phone_number: "",
            country_code: "",
            getEmails: true,
            getWhatsappNotifications: false,
            whatsapp_notification_token: "",
            isItConfirmed: false
          },
          configFor(token)
        );
      }
    }
  }
};

export const getSetupApiErrorMessage = error =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  (typeof error?.response?.data === "string" ? error.response.data : null) ||
  error?.message ||
  "Unknown error";
